(() => {
  const STYLE_URL_HINT = 'hausje-v13-nav.css';

  class HausjeV13Nav extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      this.mode = this.dataset.mode || 'desktop';
      this.state = {
        route: 'product',
        group: null,
        page: 0,
        open: false,
        menu: null,
      };
      if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
      this._load();
    }

    async _load() {
      const url = this.dataset.menuUrl;
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('menu_http_' + res.status);
        this.state.menu = await res.json();
      } catch (err) {
        console.warn('[hausje-v13-nav] menu load failed', err);
        this.shadowRoot.innerHTML = '<p style="padding:1rem;color:#6b6863">Navigatie kon niet laden.</p>';
        return;
      }
      this.state.group = this._groups()[0]?.name || null;
      this.render();
      this._bind();
      // Re-apply if theme morph clears nothing in shadow; also refresh after hydration
      requestAnimationFrame(() => this.render());
    }

    _groups() {
      return this.state.route === 'product' ? this.state.menu.product : this.state.menu.room;
    }

    _group() {
      return this._groups().find((g) => g.name === this.state.group) || this._groups()[0];
    }

    _activeLeaves(group) {
      return (group?.items || []).filter((i) => i.active && i.path);
    }

    _bind() {
      if (this._bound) return;
      this._bound = true;
      this.shadowRoot.addEventListener('click', (e) => {
        const t = e.target.closest('[data-hv13-action]');
        if (!t) return;
        const action = t.dataset.hv13Action;
        if (action === 'route') {
          this.state.route = t.dataset.route;
          this.state.group = this._groups()[0]?.name || null;
          this.state.page = 0;
          this.render();
        } else if (action === 'group') {
          this.state.group = t.dataset.group;
          if (this.mode === 'mobile') this.state.page = 1;
          this.state.open = true;
          this.render();
        } else if (action === 'toggle') {
          const route = t.dataset.route;
          if (this.state.open && this.state.route === route) this.state.open = false;
          else {
            this.state.route = route;
            this.state.group = this._groups()[0]?.name || null;
            this.state.open = true;
          }
          this.render();
        } else if (action === 'close') {
          this.state.open = false;
          this.state.page = 0;
          this.render();
          if (this.mode === 'mobile') {
            const drawer = this.closest('header-drawer');
            if (typeof drawer?.close === 'function') drawer.close();
            else drawer?.querySelector('.menu-drawer__close-button')?.click();
          }
        } else if (action === 'back') {
          this.state.page = 0;
          this.render();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.state.open) {
          this.state.open = false;
          this.render();
        }
      });

      document.addEventListener('click', (e) => {
        if (e.target.closest?.('.hv13-backdrop')) {
          this.state.open = false;
          this.render();
        }
      });
    }

    _syncScrollHints() {
      this.shadowRoot.querySelectorAll('.hv13-mega-scroll, .hv13-leaves').forEach((el) => {
        const mark = () => {
          el.dataset.atEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 8 ? 'true' : 'false';
        };
        mark();
        el.onscroll = mark;
      });
    }

    _styleLink() {
      const existing = document.querySelector(`link[href*="${STYLE_URL_HINT}"]`);
      const href = existing?.href || '';
      return href ? `<link rel="stylesheet" href="${href}">` : '';
    }

    render() {
      if (!this.state.menu || !this.shadowRoot) return;
      const backdrop = document.querySelector('.hv13-backdrop');
      if (backdrop) backdrop.classList.toggle('is-open', this.mode === 'desktop' && this.state.open);
      if (this.mode === 'desktop') this._renderDesktop();
      else this._renderMobile();
      this._syncScrollHints();
    }

    _renderDesktop() {
      const groups = this._groups();
      const group = this._group();
      const leaves = this._activeLeaves(group);
      const side = groups
        .map(
          (g) =>
            `<button type="button" class="hv13-opt ${g.name === this.state.group ? 'is-active' : ''}" data-hv13-action="group" data-group="${this._esc(g.name)}"><span>${this._esc(g.name)}</span><span aria-hidden="true">›</span></button>`
        )
        .join('');
      const leafHtml = leaves.length
        ? leaves
            .map(
              (i) =>
                `<a class="hv13-leaf" href="${this._esc(i.path)}" data-hv13-key="${this._esc(i.key)}">${this._esc(i.label)}<span aria-hidden="true">↗</span></a>`
            )
            .join('')
        : `<p class="hv13-empty">Nog geen actieve bestemmingen voor ${this._esc(group?.name || '')}. De hoofdgroep blijft zichtbaar.</p>`;

      this.shadowRoot.innerHTML = `
        ${this._styleLink()}
        <div class="hv13-toplinks" role="menubar">
          <button type="button" class="hv13-toplink ${this.state.route === 'product' && this.state.open ? 'is-active' : ''}" data-hv13-action="toggle" data-route="product" aria-expanded="${this.state.open && this.state.route === 'product'}">Shop op product <span class="hv13-chev" aria-hidden="true">▾</span></button>
          <button type="button" class="hv13-toplink ${this.state.route === 'room' && this.state.open ? 'is-active' : ''}" data-hv13-action="toggle" data-route="room" aria-expanded="${this.state.open && this.state.route === 'room'}">Shop per kamer <span class="hv13-chev" aria-hidden="true">▾</span></button>
          <a class="hv13-toplink hv13-toplink--plain" href="/pages/klein-hausje">Klein Hausje</a>
        </div>
        <div class="hv13-mega ${this.state.open ? 'is-open' : ''}" role="dialog" aria-label="Hausje navigatie">
          <aside class="hv13-mega-side">
            <p class="hv13-tiny">${this.state.route === 'product' ? 'Verken per producttype' : 'Verken per ruimte'}</p>
            <div class="hv13-mega-scroll">${side}</div>
          </aside>
          <section class="hv13-mega-center">
            <p class="hv13-tiny">${this.state.route === 'product' ? 'Shop op product' : 'Shop per kamer'}</p>
            <h2 class="hv13-mega-title">${this._esc(group?.name || '')}</h2>
            <p class="hv13-mega-desc">${this.state.route === 'product' ? 'Ontdek passende meubels per productsoort.' : `Ontdek meubels die passen bij jouw ${(group?.name || '').toLowerCase()}.`}</p>
            <div class="hv13-leaves">${leafHtml}</div>
          </section>
          <aside class="hv13-mega-photo">
            <div>
              <h3>${this.state.route === 'product' ? 'Vinden wordt eenvoudiger.' : 'Van ruimte naar vondst.'}</h3>
              <p>Twee routes. Dezelfde producten. Eén Hausje.</p>
            </div>
          </aside>
        </div>
      `;
    }

    _renderMobile() {
      const groups = this._groups();
      const group = this._group();
      const sticky = `
        <div class="hv13-mobile-sticky">
          <div class="hv13-mobile-actions">
            ${this.state.page === 1 ? `<button type="button" class="hv13-back" data-hv13-action="back">← Terug</button>` : `<span class="hv13-tiny" style="margin:0">Ontdek Hausje</span>`}
            <button type="button" class="hv13-close" data-hv13-action="close" aria-label="Menu sluiten">×</button>
          </div>
          <div class="hv13-tabs" role="tablist">
            <button type="button" class="hv13-tab ${this.state.route === 'product' ? 'is-active' : ''}" data-hv13-action="route" data-route="product">Shop op product</button>
            <button type="button" class="hv13-tab ${this.state.route === 'room' ? 'is-active' : ''}" data-hv13-action="route" data-route="room">Shop per kamer</button>
          </div>
        </div>`;

      let body = '';
      if (this.state.page === 0) {
        body = `
          <h2>${this.state.route === 'product' ? 'Wat zoek je?' : 'Welke kamer richt je in?'}</h2>
          <p class="hv13-mobile-sub">${this.state.route === 'product' ? 'Kies een productsoort en ontdek de beschikbare bestemmingen.' : 'Kies een kamer en ontdek passende meubels.'}</p>
          <div>${groups
            .map(
              (g) =>
                `<button type="button" class="hv13-row" data-hv13-action="group" data-group="${this._esc(g.name)}"><span>${this._esc(g.name)}</span><span class="chev" aria-hidden="true">›</span></button>`
            )
            .join('')}</div>`;
      } else {
        const leaves = this._activeLeaves(group);
        body = `
          <p class="hv13-crumb">${this.state.route === 'product' ? 'Shop op product' : 'Shop per kamer'}</p>
          <h2>${this._esc(group?.name || '')}</h2>
          <p class="hv13-mobile-sub">Alleen beschikbare bestemmingen zijn klikbaar.</p>
          <div>${
            leaves.length
              ? leaves
                  .map(
                    (i) =>
                      `<a class="hv13-row" href="${this._esc(i.path)}" data-hv13-key="${this._esc(i.key)}"><span>${this._esc(i.label)}</span><span class="chev" aria-hidden="true">↗</span></a>`
                  )
                  .join('')
              : `<p class="hv13-empty">Nog geen actieve bestemmingen voor deze hoofdgroep.</p>`
          }</div>`;
      }

      const info = `
        <div class="hv13-info">
          <a href="/pages/over-hausje">Over Hausje</a>
          <a href="/pages/hoe-hausje-werkt">Hoe Hausje werkt</a>
          <a href="/pages/contact">Contact</a>
          <a href="/pages/je-hausje">Je Hausje</a>
        </div>`;

      this.shadowRoot.innerHTML = `${this._styleLink()}<div class="hv13-mobile">${sticky}<div class="hv13-mobile-body">${body}${info}</div></div>`;
    }

    _esc(s) {
      return String(s ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
    }
  }

  if (!customElements.get('hausje-v13-nav')) {
    customElements.define('hausje-v13-nav', HausjeV13Nav);
  }
})();
